import { useMemo } from 'react';
import { getAvatarColor, getColorCode } from 'assets/colors';
import usePublicProfile from './usePublicProfile';
import { AvatarProps } from '@ets/design-system';
import { useGetAvatarQuery } from 'Auth/redux/avatarApi';
import { useSelector } from './redux';

type AvatarOptions = {
  showTooltip?: boolean;
  userProfile?: Profile;
};

const useAvatar = (
  userId: string,
  options: AvatarOptions,
): Pick<AvatarProps, 'ringColor' | 'imageSrc' | 'children' | 'tooltip'> => {
  const profile = usePublicProfile(userId, '', !!options?.userProfile);

  const authenticated = useSelector((state) => state.auth.authenticated);
  const { data: avatar } = useGetAvatarQuery(userId, {
    skip: !authenticated || !userId || userId === 'IMPORTED_USER' || !profile.has_avatar,
  });
  const user = options.userProfile ?? profile;

  return useMemo(() => {
    let code = getColorCode(+userId);

    let initials = user.initials;
    if (!initials) {
      initials = '  ';
    }

    if (!code || (code === 0 && initials.length > 0)) {
      code = getColorCode(initials.toLowerCase().charCodeAt(0));
    }

    return {
      ringColor: getAvatarColor(code),
      imageSrc: avatar ?? user.avatar,
      children: user.initials,
      tooltip: options.showTooltip ? { content: user.name } : undefined,
    };
  }, [userId, options, profile, avatar]);
};

export default useAvatar;
